import * as React from "react"
import { Button, Dialog, DialogContent, DialogTitle, Icon, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import {
  useResolvePersonaReferenceLinks,
  toPersonaResolvedLinkResults,
  type PersonaResolvedLinkResult,
} from "@/lib/queries/persona-opportunity-scripts.queries"

const MAX_DESCRIPTION = 10000
const MAX_LINKS = 3
const VARIATION_OPTIONS = [1, 2, 3, 4, 5] as const

/**
 * `edit` — the form; `resolving` — awaiting the resolve-links pre-step
 * (button shows "Fetching links…", inputs locked); `review` — one or more
 * links failed to resolve, so generation is gated until the user picks
 * "Generate without them" or "Cancel" back to editing.
 */
type ModalPhase = "edit" | "resolving" | "review"

/** A reference-link input row. The stable `id` is used as the React key so
 *  removing a row doesn't shift keys and swap values/focus between inputs. */
type LinkField = { id: string; value: string }

export interface PersonaSuggestScriptModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (description: string, variations: number, referenceUrls: string[]) => void
  isSubmitting?: boolean
  error?: string | null
  /** Needed to run the resolve-links pre-step when reference links are present. */
  setId: string
  tenantId: string
}

function isValidHttpUrl(value: string): boolean {
  try {
    const u = new URL(value.trim())
    return u.protocol === "http:" || u.protocol === "https:"
  } catch {
    return false
  }
}

export function PersonaSuggestScriptModal({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  error,
  setId,
  tenantId,
}: PersonaSuggestScriptModalProps) {
  const { t } = useTranslation()
  const [description, setDescription] = React.useState("")
  const [variations, setVariations] = React.useState(3)
  const [links, setLinks] = React.useState<LinkField[]>([])
  const [phase, setPhase] = React.useState<ModalPhase>("edit")
  const [reviewResults, setReviewResults] = React.useState<PersonaResolvedLinkResult[]>([])
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const resolveLinks = useResolvePersonaReferenceLinks()

  React.useEffect(() => {
    if (open) {
      setDescription("")
      setVariations(3)
      setLinks([])
      setPhase("edit")
      setReviewResults([])
      requestAnimationFrame(() => textareaRef.current?.focus())
    }
  }, [open])

  const cleanedLinks = React.useMemo(
    () => Array.from(new Set(links.map((l) => l.value.trim()).filter(Boolean))).slice(0, MAX_LINKS),
    [links],
  )
  const invalidLink = cleanedLinks.some((l) => !isValidHttpUrl(l))

  const isResolving = phase === "resolving"
  const isLocked = isSubmitting || phase !== "edit"
  const canSubmit = description.trim().length > 0 && !invalidLink && !isLocked

  const failedResults = React.useMemo(
    () => reviewResults.filter((r) => !r.ok),
    [reviewResults],
  )

  async function handleSubmit() {
    const trimmed = description.trim()
    if (!trimmed || invalidLink || isLocked) return

    const validLinks = cleanedLinks.filter(isValidHttpUrl)
    if (validLinks.length === 0) {
      onSubmit(trimmed, variations, [])
      return
    }

    setPhase("resolving")
    try {
      const result = await resolveLinks.mutateAsync({ setId, referenceUrls: validLinks, tenantId })
      const results = toPersonaResolvedLinkResults(result)
      // Represent every submitted link explicitly: a link with no matching result
      // (empty/short/malformed response) counts as unreadable rather than being
      // silently dropped — the whole point of this step is to tell the user.
      const byUrl = new Map(results.map((r) => [r.url, r]))
      const perLink: PersonaResolvedLinkResult[] = validLinks.map(
        (url) =>
          byUrl.get(url) ?? {
            url,
            ok: false,
            reason: t("personaOpportunities.scripts.suggest.linkUnchecked", {
              defaultValue: "couldn't be checked",
            }),
          },
      )
      const okUrls = perLink.filter((r) => r.ok).map((r) => r.url)
      if (okUrls.length === validLinks.length) {
        onSubmit(trimmed, variations, okUrls)
        return
      }
      setReviewResults(perLink)
      setPhase("review")
    } catch {
      // useResolveReferenceLinks already surfaces a toast on failure — just
      // unlock the form so the user can retry or edit the links.
      setPhase("edit")
    }
  }

  function handleReviewCancel() {
    setPhase("edit")
  }

  function handleGenerateWithoutFailed() {
    const okUrls = reviewResults.filter((r) => r.ok).map((r) => r.url)
    onSubmit(description.trim(), variations, okUrls)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
        <DialogContent className="sm:max-w-2xl gap-0 p-0" aria-describedby={undefined}>
        <div className="flex items-center gap-3 border-b border-border/60 px-6 py-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--ai-fill-chip)]">
            <Icon name="sparkles" size="sm" className="text-[var(--ai-text)]" />
          </div>
          <DialogTitle className="text-base font-semibold leading-snug">
            {t("personaOpportunities.scripts.suggest.title", "Suggest a script")}
          </DialogTitle>
        </div>

        {phase === "review" ? (
          <div className="flex flex-col gap-4 px-6 py-5">
            <div className="flex items-start gap-2">
              <Icon name="alert-circle" size="sm" className="mt-0.5 shrink-0 text-destructive" />
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-foreground">
                  {t("personaOpportunities.scripts.suggest.reviewTitle", {
                    defaultValue: "We couldn't read {{count}} of your links",
                    count: failedResults.length,
                  })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "personaOpportunities.scripts.suggest.reviewSubtitle",
                    "You can go back and fix the links, or generate scripts using your description and any readable links.",
                  )}
                </p>
              </div>
            </div>
            <ul className="flex flex-col gap-2">
              {failedResults.map((r) => (
                <li
                  key={r.url}
                  className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
                >
                  {t("personaOpportunities.scripts.suggest.linkFailed", {
                    defaultValue: "{{url}} — {{reason}}",
                    url: r.url,
                    reason: r.reason,
                  })}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex flex-col gap-4 px-6 py-5">
            <p className="text-sm text-muted-foreground">
              {t(
                "personaOpportunities.scripts.suggest.description",
                "Describe the script you want and how many variations to generate. We'll use your brand context to keep it on-brand.",
              )}
            </p>

            <textarea
              ref={textareaRef}
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
              placeholder={t(
                "personaOpportunities.scripts.suggest.placeholder",
                'Describe the script you want — e.g. "A 15-second UGC unboxing highlighting the waterproof zipper"',
              )}
              aria-label={t(
                "personaOpportunities.scripts.suggest.descriptionAria",
                "Script description",
              )}
              rows={4}
              disabled={isLocked}
              className={cn(
                "w-full resize-none rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none transition-[color,box-shadow]",
                "placeholder:text-muted-foreground/50",
                "focus:border-[var(--ai)] focus:ring-[3px] focus:ring-[var(--ai-fill-chip)]",
                "disabled:cursor-not-allowed disabled:opacity-50",
                error ? "border-destructive" : "border-input",
              )}
            />

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-foreground">
                {t("personaOpportunities.scripts.suggest.referenceLinksLabel", "Reference links (optional)")}
              </label>
              <p className="text-xs text-muted-foreground">
                {t(
                  "personaOpportunities.scripts.suggest.referenceLinksHelp",
                  "We'll read these public pages and use them as source material. Paywalled or login-gated pages (e.g. NYT) may not load fully.",
                )}
              </p>
              {links.map((link) => (
                <div key={link.id} className="flex items-center gap-2">
                  <input
                    type="url"
                    value={link.value}
                    onChange={(e) =>
                      setLinks((prev) =>
                        prev.map((l) => (l.id === link.id ? { ...l, value: e.target.value } : l)),
                      )
                    }
                    placeholder={t("personaOpportunities.scripts.suggest.referenceUrlPlaceholder", {
                      defaultValue: "https://…",
                    })}
                    disabled={isLocked}
                    className={cn(
                      "flex-1 rounded-md border bg-transparent px-3 py-2 text-sm outline-none",
                      link.value.trim() && !isValidHttpUrl(link.value)
                        ? "border-destructive"
                        : "border-input",
                    )}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconStart="x"
                    aria-label={t("personaOpportunities.scripts.suggest.removeLink", "Remove link")}
                    onClick={() => setLinks((prev) => prev.filter((l) => l.id !== link.id))}
                    disabled={isLocked}
                  />
                </div>
              ))}
              {links.length < MAX_LINKS && (
                <Button
                  variant="outline"
                  size="sm"
                  iconStart="plus"
                  onClick={() =>
                    setLinks((prev) => [...prev, { id: crypto.randomUUID(), value: "" }])
                  }
                  disabled={isLocked}
                  className="self-start"
                >
                  {t("personaOpportunities.scripts.suggest.addLink", "Add link")}
                </Button>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t("personaOpportunities.scripts.suggest.variationsLabel", "Variations")}
              </span>
              <div className="flex items-center gap-1.5">
                {VARIATION_OPTIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setVariations(n)}
                    disabled={isLocked}
                    aria-pressed={variations === n}
                    className={cn(
                      "h-8 w-8 rounded-md border text-sm tabular-nums transition-colors disabled:opacity-50",
                      variations === n
                        ? "border-primary/40 bg-primary/5 text-foreground"
                        : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="flex items-center gap-1.5 text-xs text-destructive">
                <Icon name="alert-circle" size="xs" />
                {error}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border/60 px-6 py-4">
          {phase === "review" ? (
            <>
              <Button variant="outline" size="sm" onClick={handleReviewCancel}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button size="sm" iconStart="sparkles" onClick={handleGenerateWithoutFailed}>
                {t("personaOpportunities.scripts.suggest.generateWithout", "Generate without them")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onClose}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                size="sm"
                iconStart={isResolving || isSubmitting ? "loader" : "sparkles"}
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={isResolving || isSubmitting ? "[&_svg]:animate-spin" : ""}
              >
                {isResolving
                  ? t("personaOpportunities.scripts.suggest.fetchingLinks", "Fetching links…")
                  : t("personaOpportunities.scripts.suggest.submit", {
                      defaultValue: "Generate {{count}}",
                      count: variations,
                    })}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
